import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { onboardingService } from '../../services/onboardingService';
import type { OnboardingStatus, OnboardingStepConfig } from '../../models/v2/onboarding';
import {
    RiUser3Line,
    RiFileTextLine,
    RiDraftLine,
    RiMailSendLine,
    RiVideoChatLine,
    RiCheckLine,
    RiArrowRightSLine
} from 'react-icons/ri';

// --- Configuration ---

const TASKS: OnboardingStepConfig[] = [
    {
        key: 'profile',
        label: 'Complete your profile',
        description: 'Add your role and location details',
        icon: <RiUser3Line className="text-xl" />,
        route: '/dashboard/profile',
        isCompleted: (s) => s.profile_completed
    },
    {
        key: 'resume',
        label: 'Upload your first resume',
        description: 'Upload your resume',
        icon: <RiFileTextLine className="text-xl" />,
        route: '/dashboard/resumes',
        isCompleted: (s) => s.first_resume_generated
    },
    {
        key: 'cover_letter',
        label: 'Generate your first cover letter',
        description: 'Write a cover letter instantly',
        icon: <RiDraftLine className="text-xl" />,
        route: '/dashboard/resumes',
        isCompleted: (s) => s.first_cover_letter_generated
    },
    {
        key: 'message',
        label: 'Send your first message',
        description: 'Network with a recruiter',
        icon: <RiMailSendLine className="text-xl" />,
        route: '/dashboard/messages',
        isCompleted: (s) => s.first_message_sent
    },
    {
        key: 'interview',
        label: 'Start your first interview',
        description: 'Practice with AI',
        icon: <RiVideoChatLine className="text-xl" />,
        route: '/interview-mode',
        isCompleted: (s) => s.first_interview_done
    }
];

const GLASS_STYLE = {
    backdropFilter: 'blur(12px)',
    background: 'rgba(255, 255, 255, 0.06)',
    border: '1px solid rgba(255, 255, 255, 0.12)',
    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.35)'
};

// --- Components ---

export default function OnboardingGuide() {
    const navigate = useNavigate();
    const [status, setStatus] = useState<OnboardingStatus | null>(() => {
        try {
            const cached = localStorage.getItem('onboarding_status_cache');
            return cached ? JSON.parse(cached) : null;
        } catch (e) {
            return null;
        }
    });

    // Only show loading state if we don't have cached data
    const [loading, setLoading] = useState(!status);

    // Initialize from localStorage to prevent flash if known to be completed
    const [isVisible, setIsVisible] = useState(() => {
        const cached = localStorage.getItem('onboarding_completed');
        return cached !== 'true';
    });

    useEffect(() => {
        fetchStatus();
    }, []);

    const fetchStatus = async () => {
        try {
            // Fetch fresh data in the background
            const data = await onboardingService.getStatus();

            // Update cache and state
            localStorage.setItem('onboarding_status_cache', JSON.stringify(data));
            setStatus(data);

            if (data.onboarding_completed) {
                // If completed, verify if we should hide it (maybe with a delay or allow user to see 100% first)
                // For now, mirroring existing logic but keeping it consistent
                setIsVisible(false);
                localStorage.setItem('onboarding_completed', 'true');
            } else {
                localStorage.setItem('onboarding_completed', 'false');
            }
        } catch (error) {
            console.error("Failed to fetch onboarding status", error);
        } finally {
            setLoading(false);
        }
    };

    if (!isVisible) return null;

    // Loading Skeleton
    if (loading) {
        return (
            <div
                className="rounded-2xl p-6 mb-8 border border-white/5 bg-white/5 backdrop-blur-sm"
            >
                {/* Header Skeleton */}
                <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
                    <div className="space-y-2">
                        <div className="h-8 w-64 bg-white/10 rounded-lg animate-pulse" />
                        <div className="h-4 w-48 bg-white/5 rounded-lg animate-pulse" />
                    </div>
                </div>

                {/* Grid Skeleton */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                    {[1, 2, 3, 4, 5].map((i) => (
                        <div key={i} className="min-h-[140px] p-4 rounded-xl border border-white/5 bg-white/5 flex flex-col justify-between">
                            <div className="flex justify-between items-start mb-3">
                                <div className="w-8 h-8 rounded-lg bg-white/10 animate-pulse" />
                            </div>
                            <div className="space-y-2">
                                <div className="h-4 w-3/4 bg-white/10 rounded animate-pulse" />
                                <div className="h-3 w-1/2 bg-white/5 rounded animate-pulse" />
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    if (!status) return null;

    // Progress Calculation
    const completedCount = status.progress.completed_count;
    const totalCount = TASKS.length;
    const progressPercent = Math.round((completedCount / totalCount) * 100);

    return (
        <div
            className="rounded-2xl p-6 mb-8 animate-fade-in transition-all duration-500"
            style={GLASS_STYLE}
        >
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-white mb-1">Get started with PitchMe.AI</h2>
                    <p className="text-gray-400 text-sm">Complete these steps to unlock the full experience</p>
                </div>

                {/* Progress Circle & Count */}
                <div className="flex items-center gap-4 text-sm font-medium">
                    <div className="w-32 h-2 bg-gray-700/50 rounded-full overflow-hidden">
                        <div
                            className="h-full bg-blue-500 transition-all duration-700 ease-out shadow-[0_0_10px_rgba(59,130,246,0.5)]"
                            style={{ width: `${progressPercent}%` }}
                        />
                    </div>
                    <div className="text-blue-400">
                        <span className="font-bold">{completedCount}/{totalCount}</span>
                    </div>
                </div>
            </div>

            {/* Task Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                {TASKS.map((task) => {
                    const isDone = task.isCompleted(status);

                    return (
                        <div
                            key={task.key}
                            onClick={() => !isDone && navigate(task.route)}
                            className={`
                                relative group p-4 rounded-xl border transition-all duration-300
                                flex flex-col justify-between min-h-[140px]
                                ${isDone
                                    ? 'border-white/5 bg-black/20 cursor-default opacity-50'
                                    : 'border-white/10 bg-white/5 hover:bg-white/10 hover:border-white/20 hover:-translate-y-1 cursor-pointer shadow-lg'
                                }
                            `}
                        >
                            {/* Top Row: Icon + Status */}
                            <div className="flex justify-between items-start mb-3">
                                <div className={`p-2 rounded-lg ${isDone ? 'bg-green-500/10 text-green-400' : 'bg-blue-500/10 text-blue-400'}`}>
                                    {isDone ? <RiCheckLine className="text-lg" /> : task.icon}
                                </div>
                                {!isDone && (
                                    <RiArrowRightSLine className="text-gray-500 group-hover:text-white transition-colors" />
                                )}
                            </div>

                            {/* Content */}
                            <div>
                                <h3 className={`font-semibold text-sm mb-1 ${isDone ? 'text-gray-400 decoration-slate-600' : 'text-white'}`}>
                                    {task.label}
                                </h3>
                                <p className="text-xs text-gray-500 leading-relaxed max-w-[95%]">
                                    {task.description}
                                </p>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
